import React from 'react';
import CallLink from '../../../common/CallLink';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import ServiceFilterPanel, {
  EMPTY_SERVICE_FILTER,
  isServiceFilterActive,
  serviceMatchesFilter,
  brandOptionsFromRows,
} from '../../../common/ServiceFilterPanel';
import { getCarTowingAllListing } from './../../../../Redux/Actions/UserAction';
import MapModalForView from '../../../common/Modal/MapModalForView';

export default function Towing() {
  const { t, i18n } = useTranslation();
  /* [CF 2026-08-17] Brand came from `carDetails.name`, the English spelling,
     so an Arabic visitor read BMW / MG. The API now returns `nameAr` too. */
  const isRTLBrand = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const brandName = (cd) =>
    (isRTLBrand ? cd?.nameAr || cd?.name : cd?.name || cd?.nameAr) || '';
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const dispatch = useDispatch();
  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  // [CF 2026-08-02] Brand comes from the row's `car` id, labelled from
  // `carDetails.name`; vehicle type from whatever owners have actually set.
  const [svcFilter, setSvcFilter] = useState({ ...EMPTY_SERVICE_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    dispatch(getCarTowingAllListing());
  }, [dispatch]);

  const home = useSelector((state) => state?.home);
  const getTowingAllList = home?.getTowingAllList;

  const towingName = (x) =>
    `${x?.user?.firstName || ''} ${x?.user?.lastName || ''}`.trim();

  const visibleList = React.useMemo(() => {
    if (!getTowingAllList) return undefined;
    let list = [...getTowingAllList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) => towingName(x).toLowerCase().includes(q));
    }
    list = list.filter((x) => serviceMatchesFilter(x, svcFilter));
    list = applySort(list, sortOption, {
      name: towingName,
      coords: userCoords,
    });
    return list;
  }, [getTowingAllList, sortOption, userCoords, search, svcFilter]);

  const brandOptions = React.useMemo(
    () => brandOptionsFromRows(getTowingAllList, isRTL),
    [getTowingAllList, isRTL]
  );

  const [mapModalOpen, setMapModalOpen] = useState(false);

  const handleOpenMap = () => {
    setMapModalOpen(true);
  };

  const handleCloseMap = () => {
    setMapModalOpen(false);
  };

  // Loading state
  if (getTowingAllList === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  // Empty state (no towing services at all)
  /* [CF 2026-08-09] An early `return` used to replace the WHOLE page with a
     "nothing found" panel whenever the list came back empty — the title, the
     search box and the filter panel went with it. On the server-filtered
     categories (Car Dealers, Rent a Car) that is a dead end: filter down to
     zero results and the controls you need to widen the filter have vanished,
     so the only way out is the browser Back button. The normal render path
     already shows an in-layout empty message beneath the header, so the early
     return is gone and that message does the job with the controls on screen.
  */
  return (
    <div>
      <div className="main-content-area">
        <div className="container">
          <h2 className="mb-2">{t('Towing')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* [CF 2026-08-02] Filter funnel — panel opens on its own row below. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isServiceFilterActive(svcFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {filtersOpen && (
            <ServiceFilterPanel
              value={svcFilter}
              onChange={setSvcFilter}
              onApply={() => setFiltersOpen(false)}
              fields={['brand']}
              brandOptions={brandOptions}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((data, i) => {
                const logo = data?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;

                //Location Data
                const gpsData =
                  typeof data?.gps === 'string'
                    ? JSON.parse(data?.gps)
                    : data?.gps;
                const lat = parseFloat(gpsData?.lat);
                const lng = parseFloat(gpsData?.long);
                const name =
                  towingName(data) || data?.carTowingName || data?.towingName;
                return (
                  <div
                    key={data?.towingOwnerId || i}
                    className="col-6 col-sm-6 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="cav-towing-card">
                      <Link
                        to={`/user-towing-details/${data?.towingOwnerId}`}
                        className="cav-towing-avatar"
                      >
                        <img
                          src={logo || 'assets/images/user/taxi/user.jpg'}
                          alt={name || ''}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'assets/images/user/taxi/user.jpg';
                          }}
                        />
                      </Link>

                      <Link
                        to={`/user-towing-details/${data?.towingOwnerId}`}
                        className="cav-towing-name"
                      >
                        {name}
                      </Link>

                      <div className="cav-towing-meta">
                        {brandName(data?.carDetails)
                          ? `${brandName(data.carDetails)} · `
                          : ''}
                        {t('Plate')} #{data?.plateNumber}
                      </div>

                      <div className="cav-towing-actions">
                        <a
                          target="_blank"
                          rel="noreferrer"
                          title="WhatsApp"
                          href={`https://wa.me/${`${data?.whatsAppNumberCountryCode}${data.whatsAppNumber}`.replace(/\D/g, '')}`}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/whatsapp.svg"
                            alt="WhatsApp"
                          />
                        </a>
                        <CallLink
                          target="_blank"
                          rel="noreferrer"
                          title="Call"
                          record={data}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/telephone.svg"
                            alt="Call"
                          />
                        </CallLink>
                        <a
                          href="javascript:void(0);"
                          role="button"
                          title="Map"
                          onClick={handleOpenMap}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/map.svg"
                            alt="Map"
                          />
                        </a>
                        <MapModalForView
                          open={mapModalOpen}
                          handleClose={handleCloseMap}
                          currentLocation={{ lat: lat, long: lng }}
                          address={data?.location}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
